/**
 * Collabora Excel Editor -- wrapper around CollabDocModifier
 *
 * Adds toolbar (connection status), conflict banner with saved version preview,
 * and relays dirty/conflict state to the workspace.
 */
import { useRef, useState, useCallback, lazy, Suspense, forwardRef, useImperativeHandle } from 'react'
import { useUndoScopeGuard } from './useUndoScopeGuard.js'
import { useSavedVersionCover } from './useSavedVersionCover.js'
import { Loader2 } from 'lucide-react'
import { CollabStatusBadge } from './CollabStatusBadge.js'
import { EditorShareMenu } from './EditorShareMenu.js'
import { EditorToolbar } from './EditorToolbar.js'
import { ConflictBanner } from './ConflictBanner.js'
import { useWopiConflict } from './useWopiConflict.js'
import type { EditorEditHandle, EditorUndoRedo } from '../client/editor-registry.js'

const LazyCollabDocModifier = lazy(() =>
  import('./CollabDocModifier.js').then(m => ({ default: m.CollabDocModifier }))
)

const LoadingSpinner = (
  <div className="flex-1 flex items-center justify-center">
    <Loader2 className="animate-spin themed-text-muted" size={24} />
  </div>
)

interface CollabExcelEditorProps {
  room: string
  fileId: string
  fileName?: string
  isActive?: boolean
  onDirtyChange?: (dirty: boolean) => void
  onConflictChange?: (conflicted: boolean) => void
  onSaveRef?: React.MutableRefObject<(() => void) | null>
  onDiscardRef?: React.MutableRefObject<(() => void) | null>
  registerUndoRedo?: (editor: EditorUndoRedo) => () => void
}

export const CollabExcelEditor = forwardRef<EditorEditHandle, CollabExcelEditorProps>(
  function CollabExcelEditor({ room, fileId, fileName, isActive, onDirtyChange, onConflictChange, onSaveRef, onDiscardRef, registerUndoRedo }, ref) {
    const editorRef = useRef<EditorEditHandle>(null)
    const refreshRef = useRef<(() => Promise<boolean>) | null>(null)
    const [connected, setConnected] = useState(false)
    const handleConnectedChange = useCallback((c: boolean) => setConnected(c), [])

    useImperativeHandle(ref, () => ({
      copy: () => editorRef.current?.copy(),
      cut: () => editorRef.current?.cut(),
      paste: () => editorRef.current?.paste(),
      delete: () => editorRef.current?.delete(),
      getSelection: () => editorRef.current?.getSelection?.() ?? null,
      focusEditor: () => editorRef.current?.focusEditor?.(),
    }), [])

    const conflict = useWopiConflict({
      fileId, onSaveRef, onDiscardRef, refreshRef, onDirtyChange, onConflictChange,
    })

  
  // While the conflict's "Saved version" preview is up, the LIVE editor is
  // still mounted behind it (display:none) — so its undo would reverse a step
  // in a document the user cannot see. Withhold its registration and claim
  // the keystroke here instead: unclaimed, it leaves the tab and reaches the
  // drive's file history. Does nothing, greys the Edit menu out.
  useUndoScopeGuard(conflict.showSavedVersion ? registerUndoRedo : undefined, true)
  // …and REPORT the cover, so Save and this editor's own commands are refused too (#930).
  useSavedVersionCover(conflict.showSavedVersion, () => conflict.setConflictView('mine'))
  return (
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Toolbar */}
        <EditorToolbar status={<><EditorShareMenu fileId={fileId} fileName={fileName} /><CollabStatusBadge connected={connected} /></>} />

        {/* Conflict banner */}
        {conflict.isConflicted && (
          <ConflictBanner
            fileId={fileId}
            view={conflict.conflictView}
            onViewChange={conflict.setConflictView}
            onDiscard={conflict.handleDiscard}
            onOverwrite={conflict.handleOverwrite}
          />
        )}

        {/* Main editor -- hidden when viewing saved version */}
        <div className="flex-1 flex flex-col overflow-hidden" style={{ display: conflict.showSavedVersion ? 'none' : undefined }}>
          <Suspense fallback={LoadingSpinner}>
            <LazyCollabDocModifier
              key={conflict.editorGeneration}
              ref={editorRef}
              room={room}
              fileId={fileId}
              fileName={fileName}
              fileType="xlsx"
              documentType="cell"
              isActive={isActive}
              onDirtyChange={conflict.handleDirtyChange}
              onSaveRef={onSaveRef}
              onRefreshRef={refreshRef}
              onDiscardRef={onDiscardRef}
              onConnectedChange={handleConnectedChange}
              registerUndoRedo={conflict.showSavedVersion ? undefined : registerUndoRedo}
            />
          </Suspense>
        </div>

        {/* Saved version preview */}
        {conflict.showSavedVersion && (
          <div className="flex-1 flex flex-col overflow-hidden">
            <Suspense fallback={LoadingSpinner}>
              <LazyCollabDocModifier
                key={conflict.conflictGeneration}
                fileId={fileId}
                fileName={fileName ? `${fileName} (Saved version)` : 'Saved version'}
                fileType="xlsx"
                documentType="cell"
                mode="readonly"
              />
            </Suspense>
          </div>
        )}
      </div>
    )
  }
)
